import React from "react";
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  MotionValue,
} from "framer-motion";

interface TimelineDotProps {
  top: string;
  progress: MotionValue<number>;
  threshold: number;
}

const TimelineDot: React.FC<TimelineDotProps> = ({
  top,
  progress,
  threshold,
}) => {
  // Map the progress to opacity: 0 before threshold, 1 after
  // We use a small range around the threshold to make it a quick transition or just a hard step
  const opacity = useTransform(progress, (value) =>
    value >= threshold ? 1 : 0
  );

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white bg-gray-200"
      style={{ top }}
    >
      <motion.div
        className="absolute inset-0 bg-[#00e9be] rounded-full"
        style={{ opacity }}
      />
    </div>
  );
};

const ScrollTimeline: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const [points, setPoints] = React.useState<
    { top: string; threshold: number; label: string }[]
  >([]);
  const [startOffset, setStartOffset] = React.useState(0);

  // Measure section positions to determine dot placement
  React.useEffect(() => {
    const calculatePositions = () => {
      const sectionIds = [
        { id: "section-why1", label: "Why VettConsult" },
        { id: "section-benefits", label: "Benefits" },
        { id: "section-comparison", label: "Comparison" },
        { id: "section-services", label: "Services" },
        { id: "section-pricing", label: "Pricing" },
        { id: "section-testimonials", label: "Testimonials" },
        { id: "section-contact", label: "Contact" },
      ];

      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const firstSection = document.getElementById(sectionIds[0].id);

      if (!firstSection || docHeight <= 0) return;

      // Calculate start offset based on the first section's position relative to max scroll
      // We want the timeline to start filling when the first section is reached
      const startPos = firstSection.offsetTop;
      const startScrollRatio = Math.max(
        0,
        Math.min(1, (startPos - window.innerHeight * 0.5) / docHeight)
      );
      setStartOffset(startScrollRatio);

      const newPoints = sectionIds
        .map((section, index) => {
          const el = document.getElementById(section.id);
          if (!el) return null;

          // Force the last point to be at the very bottom
          if (index === sectionIds.length - 1) {
            return {
              top: "100%",
              threshold: 0.99,
              label: section.label,
            };
          }

          // Calculate relative position for the dot on the timeline
          // We map the section's scroll position to the timeline's 0-100% height
          // The timeline represents the scroll range from [startScrollRatio] to [1]
          const sectionScrollPos =
            (el.offsetTop - window.innerHeight * 0.3) / docHeight;
          const relativePos =
            (sectionScrollPos - startScrollRatio) / (1 - startScrollRatio);

          // Clamp between 0 and 1
          const clampedPos = Math.max(0, Math.min(1, relativePos));

          return {
            top: `${clampedPos * 100}%`,
            threshold: clampedPos, // Threshold is relative to the timeline's internal progress (0-1)
            label: section.label,
          };
        })
        .filter(Boolean) as { top: string; threshold: number; label: string }[];

      setPoints(newPoints);
    };

    calculatePositions();
    window.addEventListener("resize", calculatePositions);
    // Recalculate after a short delay to ensure layout is stable (images loading etc)
    const timeout = setTimeout(calculatePositions, 1000);

    return () => {
      window.removeEventListener("resize", calculatePositions);
      clearTimeout(timeout);
    };
  }, []);

  // Map actual scroll progress to timeline progress
  // Timeline starts filling when we reach the startOffset
  const timelineProgress = useTransform(
    scrollYProgress,
    [startOffset, 1],
    [0, 1]
  );

  const scaleY = useSpring(timelineProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Opacity transform: appear right after Hero section
  // Hero is approximately the first viewport height, so we fade in quickly after 8-12% scroll
  const containerOpacity = useTransform(
    scrollYProgress,
    [0, 0.08, 0.12],
    [0, 0, 1]
  );

  if (points.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      className="fixed left-4 top-0 bottom-0 w-12 hidden lg:flex flex-col justify-center items-center z-50 pointer-events-none"
      style={{ opacity: containerOpacity }}
    >
      {/* The timeline container height is reduced to 50vh to be more centered and distinct from Hero */}
      <div className="relative h-[55vh] w-0.5 bg-gray-200 rounded-full overflow-visible mt-20">
        {/* Animated Progress Line */}
        <motion.div
          className="absolute top-0 left-0 right-0 bg-[#00e9be] w-full origin-top rounded-full"
          style={{ scaleY, height: "100%" }}
        />

        {/* Points */}
        {points.map((point, index) => (
          <TimelineDot
            key={index}
            top={point.top}
            progress={scaleY}
            threshold={point.threshold}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default ScrollTimeline;
